import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { RISK_STATUS_FILTER } from 'src/pages/Risks/risks.constants'
import { REQUEST_STATUS_FILTER } from 'src/pages/Requests/requests.constants'
import { RECEPTOR_STATUS_FILTER } from 'src/pages/Receptors/receptors.constants'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useHasCustomControlStatuses } from 'src/context/Gatekeeper'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { FilterName, FilterParam } from '../FilterBar.types'
import { ControlStatus } from '../../../../../lib/common/types'
import { INDEX_PAGE_WITH_FILTERS } from '../../IndexPage.constants'
import { FilterDropdown } from './FilterDropdown'

const CONTROL_STATUS_FILTER = [
  {
    value: ControlStatus.NEEDS_ATTENTION,
    label: ControlStatus.NEEDS_ATTENTION,
  },
  {
    value: ControlStatus.READY_FOR_REVIEW,
    label: ControlStatus.READY_FOR_REVIEW,
  },
  {
    value: ControlStatus.ACCEPTED,
    label: ControlStatus.ACCEPTED,
  },
  {
    value: ControlStatus.NOT_APPLICABLE,
    label: ControlStatus.NOT_APPLICABLE,
  },
]

export const StatusFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element => {
    const loc = useLocation()
    const queryParams = queryString.parse(loc.search, {
      arrayFormat: 'bracket',
    })
    const { data: customStatuses } = useControlStatuses()
    const hasCustomControlStatuses = useHasCustomControlStatuses()
    // TODO: Convert to component prop once we add a 3rd status type
    const isControls = loc.pathname.includes(INDEX_PAGE_WITH_FILTERS.CONTROLS)
    const isRisks = loc.pathname.includes(INDEX_PAGE_WITH_FILTERS.RISKS)
    const isRequests = loc.pathname.includes(INDEX_PAGE_WITH_FILTERS.REQUESTS)
    const isReceptors = loc.pathname.includes(INDEX_PAGE_WITH_FILTERS.RECEPTORS)
    const isEvidenceScansTab = queryParams.tab === 'scans'
    const isPoliciesAutomatedScanTab =
      loc.pathname.includes(INDEX_PAGE_WITH_FILTERS.POLICIES) &&
      queryParams.tab === 'automated-scan'
    const isDocumentScan = loc.pathname.includes(
      INDEX_PAGE_WITH_FILTERS.DOCUMENT_SCAN,
    )

    const statusValues = useMemo(() => {
      const values = new Array<{ value: string; label: string }>()

      if (isControls) {
        if (hasCustomControlStatuses && customStatuses) {
          values.push(
            ...customStatuses
              .getItemsList()
              .map((status: ControlStatusItem) => ({
                value: status.getId(),
                label: status.getName(),
              })),
          )
        } else {
          values.push(...CONTROL_STATUS_FILTER)
        }
      } else if (isRisks) {
        values.push(
          ...RISK_STATUS_FILTER.map(
            (status: { value: number; name: string }) => {
              return {
                value: String(status.value),
                label: status.name,
              }
            },
          ),
        )
      } else if (isRequests) {
        values.push(...REQUEST_STATUS_FILTER)
      } else if (
        isReceptors ||
        isDocumentScan ||
        isEvidenceScansTab ||
        isPoliciesAutomatedScanTab
      ) {
        values.push(...RECEPTOR_STATUS_FILTER)
      }
      return values
    }, [
      isControls,
      isRisks,
      isRequests,
      isReceptors,
      isDocumentScan,
      isEvidenceScansTab,
      isPoliciesAutomatedScanTab,
      hasCustomControlStatuses,
      customStatuses,
    ])

    return (
      <FilterDropdown
        filterParam={FilterParam.STATUS}
        filterName={
          isDocumentScan || isEvidenceScansTab || isPoliciesAutomatedScanTab
            ? FilterName.RECEPTOR_STATUS
            : FilterName.STATUS
        }
        filterValues={statusValues}
        isNested={isNested}
      />
    )
  },
)
